import { Flex, Text } from '@chakra-ui/react';
import { Button, Modal, Select } from '@morressier/alembic';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  DECISION_TEMPLATE_TYPES,
  DecisionTemplateActionType,
  DecisionTemplateType,
  IDecisionTemplatesByTypeResponse
} from '@publishing-engine/domain/models';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { FieldLabel } from 'src/components/Field';
import { fetchSubmissionDecisionTemplatesByType } from 'src/api/queries';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';

type TemplateOption = {
  templateId: string;
  label: string;
  text: string;
};

interface DecisionTemplateSelectorProps {
  submissionId: string;
  decisionType: string;
  templateType: string;
  targetFieldName: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  onTemplateSelect?: (template: TemplateOption | null) => void;
  subtypeSelected?: string;
}

const flattenTemplates = (
  response?: IDecisionTemplatesByTypeResponse,
  subtypeSelected?: string
): TemplateOption[] => {
  if (!response) return [];

  if ('templates' in response) {
    return response.templates.map(template => ({
      templateId: template.templateId,
      label: template.label,
      text: template.text
    }));
  }

  // Templates are grouped by subtype and a group can be stored without one.
  // Those are the templates saved while the journal had no subtype options
  // configured, so they are the ones to offer when no subtype is selected.
  const selectedSubtype = response.subtypes.find(
    subtype => (subtype.subtype ?? null) === (subtypeSelected || null)
  );

  if (!selectedSubtype) return [];

  return selectedSubtype.templates.map(template => ({
    templateId: template.templateId,
    label: template.label,
    text: template.text
  }));
};

const DecisionTemplateSelector = ({
  submissionId,
  decisionType,
  templateType,
  targetFieldName,
  label = 'Pre-filled Responses to Author',
  required = false,
  placeholder = 'Select a pre-filled Response to author',
  disabled = false,
  onTemplateSelect,
  subtypeSelected
}: DecisionTemplateSelectorProps) => {
  const { submissionContext } = useSubmissionData();
  const { setValue, getValues } = useFormContext();
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [pendingSelection, setPendingSelection] = useState<{
    templateId: string;
    template: TemplateOption | null;
  } | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: [
      'submission-decision-templates-by-type',
      submissionId,
      decisionType,
      templateType
    ],
    queryFn: () =>
      fetchSubmissionDecisionTemplatesByType(
        submissionId,
        decisionType,
        templateType
      ),
    enabled: Boolean(submissionId && decisionType && templateType)
  });

  // Whether this decision type has a subtype dimension (e.g. rejection reason
  // or revision severity) is determined by the config, not by the shape of the
  // templates response. The dimension only exists while the journal configures
  // options for it, so an empty option list (no rejection reason, no revision
  // severity, ...) means there is no subtype to select and the templates saved
  // without one are the ones to show.
  const subtypeKey =
    DECISION_TEMPLATE_TYPES[templateType as DecisionTemplateType]?.[
      decisionType as DecisionTemplateActionType
    ];
  const subtypeOptions = subtypeKey
    ? submissionContext?.config?.[subtypeKey]
    : undefined;
  const hasSubtypeOptions = Boolean(subtypeOptions?.length);
  const isSubtypeConfigLoading = Boolean(subtypeKey) && !submissionContext;
  const awaitingSubtype = hasSubtypeOptions && !subtypeSelected;

  const templates = useMemo(
    () => flattenTemplates(data, subtypeSelected),
    [data, subtypeSelected]
  );

  useEffect(() => {
    setSelectedTemplateId('');
    setPendingSelection(null);
  }, [subtypeSelected]);

  const applySelection = (
    templateId: string,
    template: TemplateOption | null
  ) => {
    setSelectedTemplateId(templateId);
    setValue(targetFieldName, template?.text ?? '', {
      shouldDirty: true,
      shouldValidate: true
    });

    onTemplateSelect?.(template);
  };

  const handleChange = (value: string) => {
    const selectedTemplate = templates.find(
      template => template.templateId === value
    );

    const currentValue = getValues(targetFieldName) as string | undefined;
    const hasExistingContent = Boolean(currentValue?.trim());

    if (hasExistingContent) {
      setPendingSelection({
        templateId: value,
        template: selectedTemplate ?? null
      });
      return;
    }

    applySelection(value, selectedTemplate ?? null);
  };

  const handleConfirmOverride = () => {
    if (pendingSelection) {
      applySelection(pendingSelection.templateId, pendingSelection.template);
    }
    setPendingSelection(null);
  };

  const handleCancelOverride = () => {
    setPendingSelection(null);
  };

  // Hide the entire block while the data is still fetching, when a subtype is
  // configured but not yet selected, or when there are no saved response
  // templates to select from.
  if (
    isLoading ||
    isSubtypeConfigLoading ||
    awaitingSubtype ||
    templates.length === 0
  ) {
    return null;
  }

  return (
    <>
      <Flex flexDirection="column" alignItems="flex-start" gap={2} width="100%">
        <FieldLabel
          label={label}
          subText=""
          isRequired={required}
          helpText=""
        />
        <Flex flexDirection="column" width="100%" alignItems="flex-start" gap={2}>
          <Select
            value={selectedTemplateId}
            placeholder={placeholder}
            isDisabled={disabled}
            onChange={event => handleChange(event.target.value)}
          >
            {templates.map(template => (
              <option key={template.templateId} value={template.templateId}>
                {template.label}
              </option>
            ))}
          </Select>
        </Flex>
      </Flex>

      {pendingSelection && (
        <Modal
          isOpen
          onClose={handleCancelOverride}
          width="28rem"
          closeButtonProps={{ hidden: true }}
        >
          <Flex flexDirection="column" gap={8} padding={8}>
            <Flex color="actionSecondaryCautionContent">
              <FontAwesomeIcon fontSize={32} icon={faExclamationTriangle} />
            </Flex>
            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup">Override existing content?</Text>
              <Text textStyle="textBody">
                Selecting a response will override the existing content.
              </Text>
            </Flex>
            <Flex gap={2} justifyContent="flex-end">
              <Button onClick={handleCancelOverride} variant="secondary">
                Cancel
              </Button>
              <Button onClick={handleConfirmOverride} variant="caution">
                Proceed
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}
    </>
  );
};

export default DecisionTemplateSelector;
